<template>
    <Dialog
        :value="showDialog"
        :header-text="title"
        header_classes="black white--text"
        persistent
        :width="920"
        @close_modal="closeModal"
    >
        <v-container v-if="formData && classifiersLoaded">
            <v-row no-gutters>
                <v-col cols="4" class="pr-2">
                    <v-text-field
                        v-model="formData.title"
                        :error-messages="title_error"
                        :label="$t('Supplier type')"
                        outlined
                    />
                </v-col>
                <v-col v-if="parentTypes.length > 0" cols="4">
                    <v-select
                        v-model="formData.parent_id"
                        :items="parentTypes"
                        item-text="title"
                        item-value="id"
                        :label="$t('Parent type')"
                        outlined
                        :error-messages="formData.parent_id_error"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <CustomFormEditor
                    :related-type="relatedType"
                    :clone-related-type="cloneRelatedType"
                    :account-id="accountId"
                    :external-save="true"
                    :form-data="formData"
                ></CustomFormEditor>
            </v-row>
        </v-container>

        <template v-if="formData" slot="footer">
            <v-btn v-if="formData.id" text color="error" @click="onDeleteClick">
                {{ $t("Delete") }}
            </v-btn>
            <v-spacer/>
            <v-btn text @click="closeModal">
                {{ $t("Close") }}
            </v-btn>
            <v-btn class="primary inverted--text" :loading="loading" @click="onSaveClick">
                {{ $t("Save") }}
            </v-btn>
        </template>
    </Dialog>
</template>

<script>
import Vue from "vue";
import {ref, computed} from "@vue/composition-api";
import {$t} from "@/i18n";
import SupplierModule from "../../../store/modules/supplier";
import AppModule from "../../../store/modules/app";
import Dialog from "../../../components/Dialog";
import CustomFormEditor from "../CustomField/CustomFormEditor";
import DialogModule from "../../../store/modules/dialog";
import {readCustomFields} from "../../../composables/use-custom-fields";
import errorDialog from "@/views/ErrorDialog/errorDialog"

export default {
    name: "SupplierTypeEditDialog",
    components: {
        Dialog,
        CustomFormEditor
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true
        },
        supplierTypeId: {
            type: Number,
            required: true
        },
        accountId: {
            type: Number,
            required: true
        },
        cloneTypeId: {
            type: Number,
            required: true
        }
    },
    setup(props, ctx) {
        const {
            clearValidationErrors,
            handleErrorResponse,
            fetchClassifiers,
            supplier_types,
            syncToClassifiersCache,
            removeFromClassifiersCache
        } = AppModule();
        const {fetchSupplierType, saveSupplierType, deleteSupplierType} = SupplierModule();
        const dialogModule = DialogModule();

        const formData = ref(null);
        const loading = ref(false);
        const title = ref(props.supplierTypeId ? $t("Edit supplier type") : $t("New supplier type"));
        const classifiersLoaded = ref(false);

        const parentTypes = computed(() => {
            if (supplier_types.value[props.accountId] === undefined) return [];
            let ret = supplier_types.value[props.accountId]
                .filter(x => x.id !== props.supplierTypeId && x.parent_id !== props.supplierTypeId);
            ret.splice(0, 0, {id: null, title: ""});
            return ret;
        });
        const relatedType = computed(() => {
            return "supplier.{0}".format(props.supplierTypeId);
        });
        const cloneRelatedType = computed(() => {
            if (!props.cloneTypeId) return null;
            return "supplier.{0}".format(props.cloneTypeId);
        })
        const title_error = computed(function () {
            if (formData.value == null) return null;
            if (formData.value.title_error) return formData.value.title_error;
            if (formData.value.name_error) return formData.value.name_error;
            return null;
        });

        const loadData = () => {
            if (props.supplierTypeId > 0) {
                fetchSupplierType(props.accountId, props.supplierTypeId)
                    .then(resp => {
                        formData.value = prepareFormData(resp);
                    })
                    .catch(ex => errorDialog.showError(ex));
            } else {
                if (props.cloneTypeId > 0) {
                    fetchSupplierType(props.accountId, props.cloneTypeId)
                        .then(resp => {
                            resp.id = 0;
                            resp.title = "";
                            formData.value = prepareFormData(resp);
                        })
                        .catch(ex => errorDialog.showError(ex));
                } else {
                    formData.value = prepareFormData({id: 0});
                }
            }

            fetchClassifiers({
                types: ["supplier_types", "custom_fields", "default_custom_fields"],
                account_id: props.accountId,
            })
                .then(() => classifiersLoaded.value = true)
                .catch(ex => errorDialog.showError(ex));
        };
        const prepareFormData = function (data) {
            if (data.items === undefined)
                data.items = null
            return Vue.prepare_form_data(data, {
                "title": "text",
                "parent_id": "reference",
            })
        };
        const onDeleteClick = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure you wish to delete this supplier type?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: deleteConfirmed
            });
            dialogModule.openConfirmDialog();
        };
        const deleteConfirmed = () => {
            deleteSupplierType(props.accountId, formData.value.id)
                .then(() => {
                    removeFromClassifiersCache("supplier_types", formData.value.id, props.accountId);
                    ctx.emit("close_modal");
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const onSaveClick = () => {
            clearValidationErrors(formData.value);

            let data = {
                id: formData.value.id,
                account_id: props.accountId,
                name: formData.value.name,
                title: formData.value.title,
                parent_id: formData.value.parent_id,
                items: readCustomFields(formData.value, props.accountId, relatedType.value)
            };
            saveSupplierType(data)
                .then(resp => {
                    syncToClassifiersCache("supplier_types", resp, props.accountId);

                    fetchClassifiers({
                        force: {
                            default: true,
                            types: ["custom_fields"]
                        },
                        account_id: props.accountId
                    })
                        .then(() => ctx.emit("close_modal"))
                        .catch(ex => errorDialog.showError(ex));
                })
                .catch(ex => handleErrorResponse(formData.value, ex));
        };
        const closeModal = () => {
            ctx.emit("close_modal");
        };

        loadData();

        return {
            title,
            formData,
            title_error,
            classifiersLoaded,
            parentTypes,
            relatedType,
            cloneRelatedType,
            loading,
            onSaveClick,
            onDeleteClick,
            closeModal
        };
    }
}
</script>
